import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/primitives/Spinner';
import { uploadMailingMedia } from '@/api/opengater/useMailings';
import { dragHasFiles, filesOf } from '../imageFiles';
import { mailingProblem } from './mailingErrors';
import f from './MediaField.module.css';
const MB = 1024 * 1024;
/** What goes into a mailing and up to what size — the users service checks the same. */
const LIMITS = {
    'image/jpeg': 5 * MB,
    'image/png': 5 * MB,
    'image/gif': 20 * MB,
    'video/mp4': 20 * MB,
};
const ACCEPT = Object.keys(LIMITS).join(',');
/** Why the file can't go into a mailing, or null when it can. */
function mediaFileProblem(file) {
    const max = LIMITS[file.type];
    if (max == null)
        return 'Такой файл не подойдёт: нужен JPG, PNG, GIF или MP4';
    if (file.size > max)
        return `${file.name} больше ${max / MB} МБ`;
    return null;
}
/** The picture, GIF or video of a mailing: pick or drop a file, replace it, take it off. */
export function MediaField({ value, onChange, disabled = false, }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [dragging, setDragging] = useState(false);
    const locked = disabled || uploading;
    const upload = async (files) => {
        const file = files[0];
        if (!file || locked)
            return;
        const problem = mediaFileProblem(file);
        if (problem) {
            setError(problem);
            return;
        }
        setError(null);
        setUploading(true);
        try {
            onChange(await uploadMailingMedia(file));
        }
        catch (e) {
            setError(mailingProblem(e, 'Не удалось загрузить файл.').text);
        }
        finally {
            setUploading(false);
        }
    };
    if (disabled && !value)
        return _jsx("span", { className: f.none, children: "\u0411\u0435\u0437 \u043C\u0435\u0434\u0438\u0430" });
    return (_jsxs("div", { className: f.field, children: [_jsxs("div", { className: f.drop, "data-filled": value ? true : undefined, "data-dragging": dragging || undefined, onDragOver: (e) => {
                    if (locked || !dragHasFiles(e.dataTransfer?.types))
                        return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'copy';
                    setDragging(true);
                }, onDragLeave: () => setDragging(false), onDrop: (e) => {
                    if (locked)
                        return;
                    e.preventDefault();
                    setDragging(false);
                    void upload(filesOf(e.dataTransfer?.files));
                }, children: [value ? (value.media_type === 'video' ? (_jsx("video", { className: f.thumb, src: value.url, muted: true, loop: true, playsInline: true, autoPlay: true, "aria-label": "\u0412\u0438\u0434\u0435\u043E \u0440\u0430\u0441\u0441\u044B\u043B\u043A\u0438" })) : (_jsx("img", { className: f.thumb, src: value.url, alt: "\u041C\u0435\u0434\u0438\u0430 \u0440\u0430\u0441\u0441\u044B\u043B\u043A\u0438" }))) : (_jsxs("span", { className: f.empty, children: [_jsx(Icon, { name: "image", size: 18 }), " \u041F\u0435\u0440\u0435\u0442\u0430\u0449\u0438 \u0444\u0430\u0439\u043B \u0441\u044E\u0434\u0430"] })), uploading && (_jsxs("span", { className: f.busy, children: [_jsx(Spinner, { size: 16 }), " \u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430\u2026"] }))] }), !disabled && (_jsxs("div", { className: f.actions, children: [_jsx("button", { type: "button", className: f.action, disabled: locked, onClick: () => fileRef.current?.click(), children: value ? 'Заменить' : 'Выбрать файл' }), value && (_jsx("button", { type: "button", className: f.action, disabled: locked, onClick: () => {
                            setError(null);
                            onChange(null);
                        }, children: "\u0423\u0431\u0440\u0430\u0442\u044C" }))] })), error && (_jsx("span", { className: f.error, role: "alert", children: error })), _jsx("input", { ref: fileRef, type: "file", accept: ACCEPT, hidden: true, "aria-label": "\u0424\u0430\u0439\u043B \u0434\u043B\u044F \u0440\u0430\u0441\u0441\u044B\u043B\u043A\u0438", onChange: (e) => {
                    void upload(filesOf(e.target.files));
                    e.target.value = '';
                } })] }));
}
